import { useState } from "react";
import PropTypes from "prop-types";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import MDButton from "components/MDButton";
import { AI_ASSISTANT_BLUE } from "./constants";

export default function AIChatHeader({
  sectionLabel,
  onClose,
  onNewConversation,
  disabled,
  conversationLimitReached,
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleConfirm = () => {
    setConfirmOpen(false);
    onNewConversation();
  };

  const handleNewConversationClick = () => {
    // Already at the limit — start fresh without an extra confirm step.
    if (conversationLimitReached) {
      onNewConversation();
      return;
    }
    setConfirmOpen(true);
  };

  return (
    <>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: "1px solid rgba(148,163,184,0.25)",
          bgcolor: "#ffffff",
        }}
      >
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Avatar
            sx={{
              width: 36,
              height: 36,
              background: `linear-gradient(135deg, ${AI_ASSISTANT_BLUE}, #3b82f6)`,
            }}
          >
            <AutoAwesomeRoundedIcon fontSize="small" />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight={700} sx={{ fontSize: 15, color: "#0f172a", lineHeight: 1.2 }}>
              AI Assistant
            </Typography>
            <Chip
              size="small"
              label={sectionLabel}
              sx={{
                mt: 0.4,
                height: 22,
                fontSize: 11,
                fontWeight: 600,
                bgcolor: "rgba(0, 97, 254, 0.08)",
                color: AI_ASSISTANT_BLUE,
              }}
            />
          </Box>
          <Tooltip
            title={
              conversationLimitReached
                ? "Start a new conversation (required)"
                : "New conversation"
            }
          >
            <span>
              <IconButton
                aria-label="Start new conversation"
                size="small"
                disabled={disabled}
                onClick={handleNewConversationClick}
                sx={
                  conversationLimitReached
                    ? {
                        color: AI_ASSISTANT_BLUE,
                        bgcolor: "rgba(0, 97, 254, 0.1)",
                      }
                    : undefined
                }
              >
                <RefreshRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton aria-label="Close AI assistant" size="small" onClick={onClose}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      </Box>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Start a new conversation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This clears the chat panel and starts a fresh conversation. Your previous conversations
            stay saved — nothing is deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton variant="text" color="secondary" onClick={() => setConfirmOpen(false)}>
            Cancel
          </MDButton>
          <MDButton variant="gradient" color="info" onClick={handleConfirm}>
            New conversation
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

AIChatHeader.propTypes = {
  sectionLabel: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onNewConversation: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  conversationLimitReached: PropTypes.bool,
};
